import { z } from "zod";

export const widgetSpacingKeys = [
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
] as const;
export type WidgetSpacingKey = (typeof widgetSpacingKeys)[number];
const dimensionSchema = z
  .object({ value: z.number().finite().min(0).max(7680), unit: z.enum(["px", "%"]) })
  .strict()
  .refine(
    (dimension) => dimension.unit !== "%" || dimension.value <= 100,
    "Percentage must not exceed 100",
  );
export const widgetSpacingSchema = z.partialRecord(z.enum(widgetSpacingKeys), dimensionSchema);
export type WidgetSpacing = z.infer<typeof widgetSpacingSchema>;

export function widgetSpacingStyles(
  spacing: WidgetSpacing | undefined,
): Partial<Record<WidgetSpacingKey, string>> {
  return Object.fromEntries(
    widgetSpacingKeys.flatMap((key) =>
      spacing?.[key] ? [[key, `${spacing[key].value}${spacing[key].unit}`]] : [],
    ),
  );
}

/** Read legacy CSS shorthand and side overrides without rewriting saved layouts. */
export function widgetSpacingFromStyles(
  styles: Partial<Record<string, string | number>>,
): WidgetSpacing {
  const result: WidgetSpacing = {};
  for (const kind of ["margin", "padding"] as const) {
    const parts = String(styles[kind] ?? 0)
      .trim()
      .split(/\s+/);
    const sides = [
      parts[0],
      parts[1] ?? parts[0],
      parts[2] ?? parts[0],
      parts[3] ?? parts[1] ?? parts[0],
    ];
    for (const [index, side] of ["Top", "Right", "Bottom", "Left"].entries()) {
      const key = `${kind}${side}` as WidgetSpacingKey;
      const raw = String(styles[key] ?? sides[index]);
      const match = /^(\d+(?:\.\d+)?)(px|%)?$/.exec(raw);
      result[key] = match
        ? { value: Number(match[1]), unit: match[2] === "%" ? "%" : "px" }
        : { value: 0, unit: "px" };
    }
  }
  return result;
}
